/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import {
    format,
    getPayloadObject,
    getTelemetryAttributes,
    ITelemetryContent,
    onTelemetryClick
} from '@msdyn365-commerce-modules/utilities';
import { getUrlSync, IComponent, IComponentProps, msdyn365Commerce, getCatalogId } from '@msdyn365-commerce/core';
import { ICartState } from '@msdyn365-commerce/global-state';
import { observer } from 'mobx-react';
import * as React from 'react';
import { CategoryPathLookup } from '@msdyn365-commerce/retail-proxy';
import { getCategoryPathsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { QueryResultSettingsProxy } from '@msdyn365-commerce-modules/retail-actions';

export interface ICartIconData {
    cart?: ICartState;
}

interface ICatDetails {
    prodId: number;
    category: string;
    deliveryType?: string;
}

export interface ICartIconComponentProps extends IComponentProps<ICartIconData> {
    className?: string;
    cartLabel: string;
    cartQtyLabel: string /* Upgraded to 10.0.16 */;
    telemetryContent?: ITelemetryContent;
}

export interface ICartIconComponent extends IComponent<ICartIconComponentProps> {}

const CartIconComponentActions = {};

/**
 *
 * CartIcon component
 * @extends {React.FC<ICartIconComponentProps>}
 */
const CartIcon: React.FC<ICartIconComponentProps> = observer((props: ICartIconComponentProps) => {
    const {
        cartLabel,
        data: { cart }
    } = props;
    const cartLink = getUrlSync('cart', props.context.actionContext);

    const fetchCategoryDetails = async () => {

        const cls = cart?.cart.CartLines;
        const productIds = cls?.map(cl => cl.ProductId);
        if (productIds) {
            const pids: number[] = [];
            for (const productId of productIds) {
                if (productId) {
                    pids.push(productId);
                }
            }
        
            const catPathLookUps: CategoryPathLookup[] = productIds.map(pid => { 
                return {ProductId: pid, ExtensionProperties:[] }
            });
            const cartDelType = cart?.cart.DeliveryMode;
            const categoryPaths = await getCategoryPathsAsync({
                callerContext: props.context.actionContext,
                queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(props.context.actionContext)
            }, props.context.actionContext.requestContext.apiSettings.channelId, getCatalogId(props.context.actionContext.requestContext), catPathLookUps)
            const prodCategoryDetails: ICatDetails[] = [];
            productIds.map(pid => {
                const catPathObj = categoryPaths.find(catPath => catPath.ProductId === pid);
                let catPath = '';
                let lastCategory: string | undefined = '';
                if (catPathObj && catPathObj.CategoryPath?.length) {
                    catPathObj.CategoryPath[0].Segments?.map(seg => {
                            catPath += seg.CategoryName + '>';
                            lastCategory = seg.CategoryName;
                    });
                    if (catPath.length) {
                        catPath = catPath.substring(0, catPath.length - 2);
                    }
                }
                if (pid) {
                    prodCategoryDetails.push({ prodId: pid, category: lastCategory, deliveryType: cartDelType });
                }
            });
            props.context.request.cookies.set('products_categories_details', prodCategoryDetails);
        }
    }

    /* VSI Customization - Start - 22/10/2020*/
    /* '()' small brackets for cart qunatity are removed */
    const cartItem = cart ? (cart.hasInvoiceLine ? 0 : `${cart.totalItemsInCart}`) : 0; /* Upgraded to 10.0.16 */
    /* VSI Customization - End */

    const label = format(cartLabel, cartItem) || `Shopping bag, ${cartItem} items`;
    // Construct telemetry attribute to render
    const payLoad = getPayloadObject('click', props.telemetryContent!, 'cart-icon', '');
    const attributes = getTelemetryAttributes(props.telemetryContent!, payLoad);
    fetchCategoryDetails();
    return (
        <a
            title={label}
            className={Number(cartItem) > 0 ? 'ms-header__icon-cart fill' : 'ms-header__icon-cart'}
            href={cartLink}
            aria-label={label}
            {...attributes}
            onClick={onTelemetryClick(props.telemetryContent!, payLoad, 'cart-icon')}
        >
            {/* VSI Customization - Start - 22/10/2020 */}
            {/* Custom css classes added for cart qunatity shown inside bag icon */}
            <span className='ms-header__icon-cart__count'>{Number(cartItem) > 0 ? cartItem : ''}</span>
            <span className='sr-only'>Cart</span>
            {/* VSI Customization - End */}
        </a>
    );
});

export const CartIconComponent: React.FunctionComponent<ICartIconComponentProps> = msdyn365Commerce.createComponentOverride<
    // @ts-ignore
    ICartIconComponent
>('CartIcon', { component: CartIcon, ...CartIconComponentActions });

export default CartIconComponent;
